'use strict';

var _ = require('lodash');
var d3 = require('d3');

/**
 * Single region Brush
 * @param d3Helper
 * @param brushExtentService
 * @returns {Function}
 * @constructor
 */
var BrushSparse = function BrushSparse(d3Helper, brushExtentService) {
  return function () {
    var scales, params;
    var backBrushGroup, frontBrushGroup;
    var domainRange, currentSelection, sizeRange;
    var previousExtent = '';
    var dispatch = d3.dispatch('brushchange', 'brushstart', 'brushend');

    /**
     * Constructs a new brush with no default x- and y-scale, and an empty extent.
     * Attaches event listeners to it.
     */
    function getSvgBrush() {
      var svgBrush = d3.svg.brush();
      svgBrush.on('brush', brushHandler);
      svgBrush.on('brushstart', brushStartHandler);
      svgBrush.on('brushend', brushEndHandler);
      return svgBrush;
    }

    /**
     * Creates brush group DOM element
     * @param container - parent container
     * @param prefix - brush name's prefix
     * @param brushScale
     * @param brushHeight
     */
    function createBrushGroup(container, prefix, brushScale, brushHeight) {
      var brushGroup = d3Helper.createGroupElement(container, prefix + '-brush');
      var r = brushHeight > 100 ? 6 : 0;
      var padding = prefix === 'front' ? 0 : 3;
      brushGroup.call(svgBackBrush);
      brushGroup.selectAll('rect').attr({
        rx: r,
        ry: r,
        y: -padding,
        height: brushHeight + 2 * padding
      });
      if (currentSelection !== undefined) {
        brushGroup.selectAll('rect.extent').attr({
          x: brushScale(currentSelection[0]),
          width: brushScale(currentSelection[1]) - brushScale(currentSelection[0])
        });
      }
      return brushGroup;
    }

    /**
     * Creates brush handles DOM element
     * @param brushGroup
     */
    function addHandles(brushGroup) {
      brushGroup.selectAll('.resize path').remove();
      brushGroup.selectAll('.resize').append('path').attr('d', _.wrap(scales.yScale.range()[0], d3Helper.createDraggableBars));
    }
    var svgBackBrush = getSvgBrush();

    /**
     *
     * @param container - Element to which brush should be appended
     * @constructs Brush
     */
    var brush = function brush(container) {
      var brushScale;
      if (!domainRange) {
        throw new Error('Unspecified domainRange');
      }
      brushScale = d3.scale.linear().domain(_.range(scales.xScale.range().length)).range(scales.xScale.range());
      svgBackBrush.x(brushScale);
      if (!backBrushGroup && !frontBrushGroup) {
        var brushHeight = scales.yScale.range()[0];
        backBrushGroup = createBrushGroup(container, 'back', brushScale, brushHeight);
        var handlesContainer = d3.select(container.node().parentNode).select('.post-data');
        frontBrushGroup = createBrushGroup(handlesContainer, 'front', brushScale, 0);
        addHandles(frontBrushGroup);
      }
    };

    /**
     * Changes brush's opacity according to 'opaque' class
     */
    function toggleOpacity() {
      backBrushGroup.classed('opaque', !backBrushGroup.classed('opaque'));
    }

    /**
     * Get Closes value on sparse domain.
     * @param {number []} range
     * @returns {number[]}
     */
    function getClosestRange(range) {
      var rangeList = svgBackBrush.x().range();
      return _.chain(range).map(function (rangeLimit) {
        rangeLimit = svgBackBrush.x()(rangeLimit);
        return _.reduce(rangeList, function (closest, currentRangeLimit) {
          return Math.abs(currentRangeLimit - rangeLimit) < Math.abs(closest - rangeLimit) ? currentRangeLimit : closest;
        }, 0);
      }).value();
    }

    /**
     * Convert Brush sparse data into data index.
     * @param {number[]} range
     * @returns {number[]}
     */
    function rangeToDataIndex(range) {
      var rangeList = svgBackBrush.x().range();
      range = getClosestRange(range);
      return _.map(range, function (rangeLimit) {
        return rangeList.indexOf(rangeLimit);
      });
    }

    /**
     * Convert Data Index Range on Brush sparse extent.
     * @param {number[]} dataRangeIndex
     * @returns {number[]}
     */
    function dataIndexToRange(dataRangeIndex) {
      var rangeList = svgBackBrush.x().range();
      return _.map(dataRangeIndex, function (rangeLimit) {
        return svgBackBrush.x().invert(rangeList[rangeLimit]);
      });
    }

    /**
     * Adjust Range to Domain Values
     * @param {number[]} range
     * @returns {number[]}
     */
    function snapBrushToSparse(range) {
      var rangeList = svgBackBrush.x().range();
      range = getClosestRange(range, rangeList);
      if (range[0] === range[1]) {
        var indexRangeList = rangeList.indexOf(range[1]);
        if (rangeList[indexRangeList + 1]) {
          range[1] = rangeList[indexRangeList + 1];
        } else {
          range[0] = rangeList[indexRangeList - 1];
        }
      }
      return _.map(range, svgBackBrush.x().invert);
    }

    /**
     * Handler called on 'brushchange' event
     */
    function brushHandler() {
      var domain = svgBackBrush.x().domain();
      var domainLength = domain[1];
      var extent = svgBackBrush.extent().join('');
      if (previousExtent !== extent) {
        previousExtent = svgBackBrush.extent().join('');
        var snappedRange = snapBrushToSparse(svgBackBrush.extent());
        snappedRange = brushExtentService.adjustBrushToPermittedSize(sizeRange, currentSelection, snappedRange, domainLength);
        brush.setSelection(rangeToDataIndex(snappedRange));
      } else {
        brush.setSelection(currentSelection);
      }
    }

    /**
     * Handler called on 'brushchange' event
     */
    function brushStartHandler() {
      //stops the d3 event from passing to the parent container(carousel)
      d3.event.sourceEvent.stopPropagation();
      toggleOpacity();
      dispatch.brushstart(rangeToDataIndex(currentSelection));
    }

    /**
     * Handler called on 'brushend' event
     */
    function brushEndHandler() {
      toggleOpacity();
      dispatch.brushend(rangeToDataIndex(currentSelection));
    }

    /**
     * Checks if there's an initial selection
     * @returns {boolean}
     */
    brush.isSelectionInitialized = function () {
      return !_.isEqual(brush.getSelection(), [0, 0]);
    };

    /**
     * Updates brush (front and back) extent
     * @param {number[]} range
     */
    brush.updateBrush = function (range) {
      range = range || currentSelection;
      if (range) {
        var extent = svgBackBrush.extent(range);
        backBrushGroup.call(extent);
        frontBrushGroup.call(extent);
      }
    };

    /**
     * Returns current selection
     * @returns {number[]}
     */
    brush.getSelection = function () {
      return svgBackBrush.extent();
    };

    /**
     * Sets brush selection
     * @param {number[]} range
     * @param {boolean} [skipDispatchingEvent] - should 'brushchange' be fired
     */
    brush.setSelection = function (range, skipDispatchingEvent) {
      range = dataIndexToRange(range);
      brush.updateBrush(range);
      if (!skipDispatchingEvent) {
        dispatch.brushchange(rangeToDataIndex(range), currentSelection, d3.event);
      }
      currentSelection = range;
    };

    /**
     * Sets domainRange to value
     * @param {boolean} value
     * @returns {brush}
     */
    brush.withDomainRange = function (value) {
      domainRange = value;
      return brush;
    };

    /**
     * Sets sizeRange to value
     * @param {boolean} value
     * @returns {brush}
     */
    brush.withSizeRange = function (value) {
      sizeRange = value;
      return brush;
    };

    /**
     * Sets brush's scales
     * @param {boolean} value
     * @returns {brush}
     */
    brush.scales = function (value) {
      scales = value;
      return brush;
    };

    /**
     * Clears the extent, making the brush extent empty
     */
    brush.clear = function () {
      svgBackBrush.clear();
    };
    brush.withParams = function (value) {
      params = value;
      return brush;
    };
    brush.destroy = function () {
      svgBackBrush.on('brush', null);
      svgBackBrush.on('brushstart', null);
      svgBackBrush.on('brushend', null);
    };
    return d3.rebind(brush, dispatch, 'on');
  };
};
BrushSparse.$inject = ['d3Helper', 'BrushExtentService'];
module.exports = BrushSparse;